import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * MultiSelect - Custom multi-select dropdown (div-based, no native <select>)
 * Follows the same pattern as NativeSelect to avoid platform injection bugs.
 */
export function MultiSelect({
  values = [],
  onValuesChange,
  placeholder = "Selecione...",
  options = [],
  disabled = false,
  loading = false,
  className = "",
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const isDisabled = disabled || loading;

  const selectedOptions = options.filter((opt) => values.includes(opt.value));

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") { setIsOpen(false); setSearch(""); }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  const toggleOption = (optValue) => {
    const newValues = values.includes(optValue)
      ? values.filter((v) => v !== optValue)
      : [...values, optValue];
    onValuesChange(newValues);
  };

  const removeOption = (optValue, e) => {
    e.stopPropagation();
    onValuesChange(values.filter((v) => v !== optValue));
  };

  const filteredOptions = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div ref={containerRef} className={cn("relative", className)} {...props}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => !isDisabled && setIsOpen((p) => !p)}
        className={cn(
          "w-full min-h-[40px] px-3 py-1.5 text-sm rounded-md border border-input bg-background",
          "flex items-center justify-between gap-2 text-left",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          loading && "animate-pulse"
        )}
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {selectedOptions.length === 0 && (
            <span className="text-muted-foreground py-0.5">
              {loading ? "Carregando..." : placeholder}
            </span>
          )}
          {selectedOptions.map((opt) => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs"
            >
              {opt.label}
              <X
                className="h-3 w-3 cursor-pointer hover:text-red-500"
                onClick={(e) => removeOption(opt.value, e)}
              />
            </span>
          ))}
        </div>
        {loading ? (
          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin shrink-0" />
        ) : (
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        )}
      </button>

      {isOpen && (
        <div
          ref={listRef}
          className={cn(
            "absolute z-[9999] w-full mt-1 bg-popover border border-input rounded-md shadow-lg",
            "animate-in fade-in-0 zoom-in-95 duration-100"
          )}
        >
          {options.length > 5 && (
            <div className="p-2 border-b">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="w-full px-2 py-1.5 text-sm border rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                autoFocus
              />
            </div>
          )}
          <div
            className="max-h-[200px] overflow-y-auto overscroll-contain py-1"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {filteredOptions.length === 0 ? (
              <div className="py-3 text-center text-sm text-muted-foreground">
                Nenhuma opção encontrada
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = values.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleOption(option.value)}
                    className={cn(
                      "w-full px-3 py-2.5 text-sm text-left",
                      "flex items-center justify-between",
                      "hover:bg-accent hover:text-accent-foreground",
                      "active:bg-accent/80 touch-manipulation",
                      isSelected && "bg-accent/50 font-medium"
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary shrink-0 ml-2" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
